import useSWR from 'swr';
import {
  AmplifyGet,
  AmplifyPost,
} from '@sunrun/sales-experience-shared/amplify-api-wrapper';
import { SplatUserType } from '@sunrun/sales-experience-shared';
import {
  mapReferralProspectsToProspects,
  type ReferralProspectsApiRow,
} from 'helpers/referralHelpers';
import { resolveReferralProspectsSalesReps } from 'helpers/pipelineSalesRepsStorage';
import { useHybridToken } from 'providers/Auth';
import { logger } from 'providers/logger';
import { useUserProfile } from 'providers/UserProfile';
import { Prospect } from 'types/prospect';

const fetchReferralProspects = async (
  hybridToken: string,
  splatUserType: SplatUserType | undefined,
  contactId: string | undefined,
): Promise<Prospect[]> => {
  if (splatUserType === 'CX Pro') {
    const response = await AmplifyGet(
      'Get Referral Prospects',
      'SalesExpDashboardApi',
      '/referral-prospects',
      {
        headers: {
          Authorization: `Bearer ${hybridToken}`,
        },
        queryStringParameters: { cx_pro: 'true' },
      },
      logger,
      {
        expectedErrorCodes: [401, 403],
      },
    );

    if (Array.isArray(response)) {
      return mapReferralProspectsToProspects(
        response as ReferralProspectsApiRow[],
      );
    }
    return [];
  }

  const salesReps = resolveReferralProspectsSalesReps(contactId);
  if (!salesReps?.length) {
    return [];
  }

  const response = await AmplifyPost<ReferralProspectsApiRow[]>(
    'Get Referral Prospects',
    'SalesExpDashboardApi',
    '/referral-prospects',
    {
      headers: {
        Authorization: `Bearer ${hybridToken}`,
      },
      body: { salesReps },
    },
    logger,
    {
      expectedErrorCodes: [401, 403],
    },
  );

  if (Array.isArray(response)) {
    return mapReferralProspectsToProspects(response);
  }
  return [];
};

const useReferralProspects = (): {
  referrals: Prospect[];
  isLoading: boolean;
  error: Error | undefined;
} => {
  const { hybridToken } = useHybridToken();
  const { userProfile, splatUserType } = useUserProfile();

  const salesRepsKey = resolveReferralProspectsSalesReps(
    userProfile?.contactId,
  )?.join(',');

  const shouldFetch = Boolean(hybridToken && userProfile);

  const { data, error, isLoading } = useSWR(
    shouldFetch
      ? ['/referral-prospects', hybridToken, splatUserType, salesRepsKey]
      : null,
    ([, token, userType]) =>
      fetchReferralProspects(
        token as string,
        userType as SplatUserType | undefined,
        userProfile?.contactId,
      ),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  return {
    referrals: data ?? [],
    isLoading: shouldFetch ? isLoading : false,
    error,
  };
};

export { fetchReferralProspects, useReferralProspects };
